import { Plug, Settings, Zap } from "lucide-react";
import { motion } from "framer-motion";
import backgroundPattern from "@/assets/svg/ooorganize.svg";
import { useTranslation } from "next-i18next";
import { Cta } from "@/components/Cta";

const steps = [
  {
    icon: Plug,
    index: 0
  },
  {
    icon: Settings,
    index: 1
  },
  {
    icon: Zap,
    index: 2
  },
];

const HowItWorks = () => {
  const { t } = useTranslation('home');

  return (
    <motion.section
      className="pt-20 md:pt-24"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true, margin: "-50px" }}
    >
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16 md:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-8">
            {t('howItWorks.title')}
          </h2>
          <p className="text-xl md:text-2xl text-secondary-foreground max-w-4xl mx-auto leading-relaxed">
            {t('howItWorks.subtitle')}
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto">
          {/* Steps */}
          <motion.div 
            className="grid md:grid-cols-3 gap-8 md:gap-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            {steps.map((step, index) => (
              <motion.div
                key={`step-${index}`}
                className="flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                viewport={{ once: true }}
              >
                {/* Icon */}
                <div className="w-32 h-32 bg-container-background border border-border rounded-full flex items-center justify-center mb-6 relative z-10 shadow-lg overflow-hidden solution-card-border light_effect_circular">
                  {/* Background pattern */}
                  <div
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                      backgroundImage: `url(${backgroundPattern.src})`,
                      backgroundPosition: "center",
                      backgroundRepeat: "repeat",
                      backgroundSize: "80px",
                      opacity: 0.15,
                      filter: "brightness(2) contrast(0.8)",
                      maskImage: "radial-gradient(circle at center, rgba(0,0,0,0) 30%, rgba(0,0,0,1) 60%, rgba(0,0,0,0.5) 80%, rgba(0,0,0,0) 100%)",
                      WebkitMaskImage: "radial-gradient(circle at center, rgba(0,0,0,0) 30%, rgba(0,0,0,1) 60%, rgba(0,0,0,0.5) 80%, rgba(0,0,0,0) 100%)"
                    }}
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 rounded-full pointer-events-none card-gradient-overlay"></div>

                  {/* Icon */}
                  <div className="relative z-10">
                    <step.icon className="w-12 h-12 text-electric-blue" strokeWidth={1.2} />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold mb-3 text-primary-foreground">{t(`howItWorks.steps.${step.index}.title`)}</h3>
                <p className="text-secondary-foreground text-lg">{t(`howItWorks.steps.${step.index}.description`)}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Deployment Options */}
          <motion.div
            className="mt-16 flex flex-col items-center gap-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="flex flex-col sm:flex-row items-center gap-4 bg-container-background border border-border rounded-full px-6 sm:px-8 py-4 light_effect_rounded_full">
              <span className="text-secondary-foreground text-center sm:text-left">{t('howItWorks.deployment.label')}</span>
              <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
                {[0, 1, 2].map((idx) => (
                  <span key={idx} className="px-4 py-1 bg-electric-blue/10 text-electric-blue rounded-full text-sm font-medium whitespace-nowrap">
                    {t(`howItWorks.deployment.options.${idx}`)}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <Cta size="large">
              {t('howItWorks.cta', { defaultValue: 'Start now' })}
            </Cta>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default HowItWorks;